import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { BOOTSTRAP_BREAKPOINTS } from '@skippay/core/styles/media';
import { getResponsiveStyles } from '@skippay/core/styles/responsivity';
import { size } from '@skippay/core/styles/spacing';
import { color, definitions, radius, transition } from '@skippay/core/styles/variables';
import { shouldForwardProp } from '@skippay/core/utils/emotionUtils';

import { Box } from './@types/box';

const getDarkBgColor = (
	bgColor: keyof (typeof definitions)['colorDark']
): keyof (typeof definitions)['colorDark'] => {
	switch (bgColor) {
		case 'blue-50':
			return 'blue-500';
		case 'green-50':
			return 'green-500';
		case 'blue-100':
			return 'blue-500';
		default:
			return 'gray-900';
	}
};

export const SBox = styled(`div`, {
	shouldForwardProp: (prop) =>
		shouldForwardProp(
			[
				'bgColor',
				'bgOpacity',
				'border',
				'borderRadius',
				'darkModeBorder',
				'fullHeight',
				'innerSpace',
				'mobileFullWidth',
				'noShadow',
				'overflowVisible',
			],
			prop
		),
})<Box>`
	position: relative;
	overflow: ${({ overflowVisible }) => (overflowVisible ? 'visible' : 'hidden')};
	transition: border ${transition('base')};
	border-radius: ${radius('small')};
	${({ noShadow }) =>
		!noShadow &&
		css`
			box-shadow: 0 4px 8px 0 rgba(55, 71, 79, 0.1);
		`};
	background-color: ${({ bgColor, bgOpacity }) => color(bgColor ?? 'white', 'light', bgOpacity)};
	${({ fullHeight }) =>
		fullHeight &&
		css`
			display: flex;
			flex-direction: column;
			flex: 1;
			height: 100%;
		`};
	${({ borderRadius }) =>
		getResponsiveStyles(borderRadius ?? 'md', {
			sm: { borderRadius: radius('small') },
			md: { borderRadius: radius('medium') },
			lg: { borderRadius: radius('large') },
			none: { borderRadius: 0 },
		})};
	${({ border }) =>
		border &&
		css`
			border-style: solid;
			border-width: ${typeof border.thickness === 'number'
				? `${border.thickness}px`
				: border.thickness
					? `${border.thickness[0]}px ${border.thickness[1]}px ${border.thickness[2]}px ${border.thickness[3]}px`
					: '1px'};
			border-color: ${color(border.color ?? 'light-gray-200')};
		`}

	${({ innerSpace }) => {
		return getResponsiveStyles(innerSpace ?? 'none', {
			lg: { padding: size(8) },
			md: { padding: size(4) },
			sm: { padding: size(2) },
			none: {},
		});
	}}

	.dark & {
		background-color: ${color('light-gray-100', 'dark')};
		${({ bgColor }) =>
			bgColor &&
			css`
				background: ${color(getDarkBgColor(bgColor), 'light', 0.2)};
				border: 1px solid ${color(getDarkBgColor(bgColor))};
			`}
		${({ darkModeBorder }) =>
			darkModeBorder &&
			css`
				border-style: solid;
				border-width: ${typeof darkModeBorder.thickness === 'number'
					? `${darkModeBorder.thickness}px`
					: darkModeBorder.thickness
						? `${darkModeBorder.thickness[0]}px ${darkModeBorder.thickness[1]}px ${darkModeBorder.thickness[2]}px ${darkModeBorder.thickness[3]}px`
						: '1px'};
				border-color: ${darkModeBorder.color
					? color(darkModeBorder.color, 'dark')
					: color('gray-200', 'dark')};
			`}
	}

	${({ mobileFullWidth }) =>
		mobileFullWidth &&
		css`
			@media (max-width: ${BOOTSTRAP_BREAKPOINTS.SM}px) {
				margin-left: -15px;
				margin-right: -15px;
			}
		`}

	@media print {
		padding-left: 0;
		padding-right: 0;
		box-shadow: none;
		border-radius: 0;
		max-width: 100%;
	}
`;
